import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersDeWaterkantPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'De Waterkant Painters | Professional Painting Services | paintersdewaterkant.co.za',
    description: 'Expert painting services in De Waterkant with 17+ years experience. Residential, commercial painting, interior and exterior. Free quotes, quality workmanship, competitive rates. Serving De Waterkant, Green Point, and City Bowl.',
    keywords: 'painters De Waterkant, painting services De Waterkant, house painters De Waterkant, commercial painters De Waterkant, residential painting De Waterkant',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters De Waterkant',
          description: 'Professional painting services in De Waterkant with over 17 years of experience',
          address: 'De Waterkant, Western Cape',
          priceRange: '$$',
          serviceArea: ['De Waterkant', 'Green Point', 'Sea Point', 'City Bowl', 'Tamboerskloof']
        }),
        generateServiceSchema({
          name: 'Urban Painting Services',
          description: 'Residential and commercial painting services for urban properties',
          provider: 'Painters De Waterkant',
          areaServed: ['De Waterkant', 'Green Point', 'City Bowl'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Heritage Buildings']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-paint-brush-line text-2xl text-[#e9f638]"></i>
              <span className="text-xl font-bold">Cape Town Painters</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get a Quote</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/81889c7a2043df5dfeda0fb94a5b137f.jpg"
            alt="Painters De Waterkant - Professional Painting Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Painters De Waterkant</h1>
          <p className="text-xl md:text-2xl mb-6">Premium Painting Services in De Waterkant & City Bowl</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="http://paintersdewaterkant.co.za" target="_blank" rel="noopener noreferrer" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Visit Website</a>
            <Link to="/get-a-quote" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
          </div>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#e9f638] py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-black">17+</div>
              <div className="text-black font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">3500+</div>
              <div className="text-black font-medium">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">99%</div>
              <div className="text-black font-medium">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">Urban</div>
              <div className="text-black font-medium">Specialists</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">About Painters De Waterkant</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters De Waterkant</strong> has been the premier choice for professional painting services in De Waterkant, Green Point, and the greater City Bowl area for over 17 years. Our deep understanding of urban painting requirements, combined with our commitment to quality craftsmanship and customer satisfaction, has established us as the trusted painting partner for residential and commercial properties in one of Cape Town's most vibrant and sought-after neighborhoods.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Heritage and Urban Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                Founded in 2007, <strong>Painters De Waterkant</strong> was established to serve the unique needs of property owners in this historic and rapidly developing area. Over the past 17 years, we have completed over 3,500 painting projects throughout De Waterkant, Green Point, Sea Point, Mouille Point, Tamboerskloof, Gardens, and surrounding City Bowl suburbs. Our experience spans from heritage Victorian homes and modern apartments to trendy restaurants, boutique hotels, office spaces, and commercial properties that define this cosmopolitan neighborhood.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                De Waterkant presents unique painting challenges due to its urban density, architectural diversity, heritage building requirements, proximity to the ocean, and the high standards expected by discerning property owners and businesses. Our team has developed specialized expertise in addressing these challenges, working efficiently in confined urban spaces, respecting heritage building guidelines, coordinating with body corporates and property managers, minimizing disruption to neighbors and businesses, and delivering premium finishes that meet the sophisticated aesthetic standards of the area.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Comprehensive Urban Painting Services</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters De Waterkant</strong>, we offer a complete range of painting services tailored to meet the diverse needs of urban property owners. Our residential painting services include apartment interior painting, townhouse painting, heritage home restoration, balcony and patio painting, exterior building painting, body corporate painting projects, and color consultation services. We understand the unique requirements of urban living and work efficiently to minimize disruption while delivering exceptional results.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                For commercial clients, we provide professional painting solutions that enhance brand image, create inviting atmospheres, and maintain property values in this competitive market. Our commercial services include restaurant and café painting, boutique hotel painting, office space painting, retail store painting, guesthouse and B&B painting, building exterior painting, and specialized finishes for hospitality venues. We understand the importance of maintaining business operations and work flexible hours to accommodate your schedule.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Heritage Building Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                One of our core specializations is working with heritage buildings and properties in conservation areas. <strong>Painters De Waterkant</strong> has extensive experience navigating heritage regulations, using appropriate materials and techniques for historic buildings, restoring original features and finishes, working with heritage consultants and authorities, and maintaining the architectural character of historic properties. We understand the importance of preserving Cape Town's architectural heritage while meeting modern performance standards.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our heritage painting services include careful assessment of existing finishes and substrates, selection of appropriate traditional and modern materials, restoration of decorative elements and features, color matching to original or period-appropriate schemes, and documentation for heritage authorities when required. We take pride in our role in preserving the unique character of De Waterkant's historic buildings while ensuring they meet contemporary standards for durability and performance.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Premium Materials and Finishes</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters De Waterkant</strong>, we understand that the sophisticated properties in this area demand premium materials and exceptional finishes. We partner with leading paint manufacturers to source high-quality products including premium interior paints with superior coverage and durability, specialized exterior coatings for coastal urban environments, eco-friendly low-VOC and zero-VOC paints, designer finishes and specialty coatings, heritage-appropriate traditional paints, and high-performance commercial-grade products. Our extensive product knowledge allows us to recommend the best solutions for your specific requirements.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We offer a comprehensive range of finishes to suit every aesthetic preference and functional requirement, from classic matte and eggshell finishes to contemporary satin and semi-gloss options, specialized textured finishes, metallic and pearlescent effects, and custom color matching services. Our color consultation service helps you choose colors and finishes that complement your property's architecture, enhance natural light, and create the desired atmosphere for your space.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Professional Process</h3>
              <p className="text-lg leading-relaxed mb-6">
                Every project at <strong>Painters De Waterkant</strong> follows a systematic process designed to ensure exceptional results and complete customer satisfaction. We begin with a detailed consultation and site assessment where we evaluate your property, discuss your vision and requirements, assess any special considerations such as heritage requirements or body corporate regulations, provide expert recommendations, and develop a comprehensive project plan with clear timelines and costs.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our preparation phase is thorough and comprehensive, including protection of floors, furniture, and fixtures, careful masking of areas not being painted, surface cleaning and preparation, repair of cracks, holes, and imperfections, sanding and priming as required, and coordination with building management or neighbors as needed. We understand that preparation is the foundation of quality painting work and never compromise on this critical phase.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                During the painting phase, our skilled craftsmen apply coatings using proven techniques that ensure even coverage, smooth finishes, clean lines and edges, and professional results throughout. We maintain a clean, organized work environment, respect your property and neighbors, work efficiently to minimize disruption, and maintain open communication throughout the project. Our attention to detail extends to every aspect of the work, from cutting in around architectural features to achieving perfectly smooth finishes on walls and ceilings.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Upon completion, we conduct a thorough final inspection with you to ensure every detail meets your expectations. We perform any necessary touch-ups, remove all protective materials, clean the work area thoroughly, dispose of waste responsibly, and provide maintenance recommendations. We don't consider a project complete until you're completely satisfied with the results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Service Areas Throughout the City Bowl</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters De Waterkant</strong> proudly serves De Waterkant and the entire City Bowl area. Our comprehensive service coverage includes De Waterkant, Green Point, Sea Point, Mouille Point, Three Anchor Bay, Tamboerskloof, Gardens, Oranjezicht, Higgovale, Vredehoek, Zonnebloem, Woodstock, Salt River, Observatory, and surrounding central Cape Town suburbs. Our central location allows us to respond quickly to inquiries, provide prompt service, and offer ongoing support to our clients throughout the area.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Working with Body Corporates and Property Managers</h3>
              <p className="text-lg leading-relaxed mb-6">
                We have extensive experience working with body corporates, property managers, and homeowners associations. <strong>Painters De Waterkant</strong> understands the requirements and procedures for painting projects in sectional title properties, including obtaining necessary approvals, coordinating with building management, scheduling work to minimize disruption to residents, complying with building rules and regulations, providing required documentation and insurance certificates, and maintaining professional standards throughout the project. We make the process smooth and hassle-free for all parties involved.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Environmental Responsibility</h3>
              <p className="text-lg leading-relaxed mb-6">
                As a company operating in one of Cape Town's most environmentally conscious neighborhoods, <strong>Painters De Waterkant</strong> takes environmental responsibility seriously. We are committed to sustainable painting practices including using eco-friendly paints with low or zero VOC content, proper disposal of paint waste and materials, minimizing water usage and preventing runoff, recycling paint containers and materials, using energy-efficient equipment, and supporting local environmental initiatives. We believe in protecting the environment while delivering exceptional painting results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Customer Satisfaction Guarantee</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters De Waterkant</strong>, your complete satisfaction is our ultimate goal. We stand behind the quality of our work with comprehensive warranties and guarantees. Our 99% customer satisfaction rating reflects our commitment to excellence and our dedication to exceeding client expectations on every project. We maintain open communication throughout the project, address any concerns promptly, and ensure the final result meets or exceeds your expectations.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We understand that inviting contractors into your home or business requires trust. That's why we maintain the highest standards of professionalism, reliability, and integrity in all our interactions. Our team members are courteous, respectful, and committed to providing a positive experience from initial consultation through project completion. We treat every property as if it were our own, taking care to protect your belongings and maintain a clean, safe work environment.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Competitive Pricing and Premium Value</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters De Waterkant</strong> offers competitive pricing without compromising on quality. We provide detailed, transparent quotes that clearly outline all costs, materials, and services, so you know exactly what to expect. There are no hidden fees or surprise charges. Our pricing reflects the quality of materials we use, the expertise of our team, and the professional service we provide. We believe in delivering exceptional value by combining quality workmanship, premium materials, and outstanding customer service at fair, competitive rates.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Get Started with Your Painting Project</h3>
              <p className="text-lg leading-relaxed mb-6">
                Ready to transform your De Waterkant property with professional painting services? Contact <strong>Painters De Waterkant</strong> today for a free, no-obligation quote. Our friendly team is ready to discuss your project, answer your questions, and provide expert guidance on all your painting needs. With over 17 years of urban painting experience, thousands of satisfied customers, and a commitment to excellence, we're the painting partner you can trust to deliver outstanding results that enhance your property's beauty and value.
              </p>

              <div className="bg-[#e9f638] p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <p className="flex items-center text-lg">
                    <i className="ri-global-line text-2xl mr-3"></i>
                    <a href="http://paintersdewaterkant.co.za" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">paintersdewaterkant.co.za</a>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-map-pin-line text-2xl mr-3"></i>
                    <span className="font-medium">Serving De Waterkant & City Bowl</span>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-time-line text-2xl mr-3"></i>
                    <span className="font-medium">Monday - Saturday: 7:00 AM - 6:00 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Painting Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20apartment%20interior%20painting%20De%20Waterkant%20fresh%20painted%20walls%20elegant%20furniture%20natural%20lighting%20professional%20finish%20simple%20background&width=400&height=300&seq=service-residential-dw&orientation=landscape"
                    alt="Residential Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Residential Painting</h3>
                  <p className="text-gray-700 mb-4">Complete interior and exterior painting for apartments, townhouses, and homes in urban settings.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Apartment interior painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Townhouse painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Balcony painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building exteriors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=commercial%20restaurant%20interior%20painting%20De%20Waterkant%20modern%20design%20professional%20painters%20working%20clean%20organized%20simple%20background&width=400&height=300&seq=service-commercial-dw&orientation=landscape"
                    alt="Commercial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Commercial Painting</h3>
                  <p className="text-gray-700 mb-4">Professional painting for restaurants, hotels, offices, and retail spaces with minimal disruption.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Restaurant painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Boutique hotel painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Office painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Retail space painting</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=heritage%20building%20restoration%20painting%20Victorian%20architecture%20De%20Waterkant%20historic%20property%20professional%20work%20simple%20background&width=400&height=300&seq=service-heritage-dw&orientation=landscape"
                    alt="Heritage Building Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Heritage Building Painting</h3>
                  <p className="text-gray-700 mb-4">Specialized painting services for heritage buildings and properties in conservation areas.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Victorian home restoration</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Period-appropriate finishes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Heritage compliance</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Feature restoration</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=exterior%20building%20painting%20urban%20property%20De%20Waterkant%20modern%20architecture%20professional%20finish%20city%20view%20simple%20background&width=400&height=300&seq=service-exterior-dw&orientation=landscape"
                    alt="Exterior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-gray-700 mb-4">High-quality exterior painting for urban properties with coastal protection coatings.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building facades</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Balcony painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Waterproofing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=interior%20room%20painting%20modern%20apartment%20De%20Waterkant%20elegant%20design%20fresh%20colors%20professional%20finish%20simple%20background&width=400&height=300&seq=service-interior-dw&orientation=landscape"
                    alt="Interior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Interior Painting</h3>
                  <p className="text-gray-700 mb-4">Beautiful interior painting services using premium paints for lasting beauty and durability.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Ceiling painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trim and molding</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Feature walls</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=color%20consultation%20paint%20samples%20swatches%20interior%20design%20De%20Waterkant%20planning%20professional%20advice%20simple%20background&width=400&height=300&seq=service-consultation-dw&orientation=landscape"
                    alt="Color Consultation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Color Consultation</h3>
                  <p className="text-gray-700 mb-4">Expert color consultation to help you choose perfect colors for your urban property.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Color scheme design</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Sample testing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Designer finishes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trend guidance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Work Gallery</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautifully%20painted%20modern%20apartment%20interior%20De%20Waterkant%20city%20view%20fresh%20walls%20elegant%20furniture%20natural%20lighting%20simple%20background&width=400&height=500&seq=gallery-dw-1&orientation=portrait"
                  alt="Apartment Interior Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20building%20exterior%20freshly%20painted%20De%20Waterkant%20urban%20property%20professional%20finish%20city%20setting%20simple%20background&width=400&height=500&seq=gallery-dw-2&orientation=portrait"
                  alt="Exterior Building Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20restaurant%20interior%20painted%20De%20Waterkant%20trendy%20design%20professional%20finish%20dining%20space%20simple%20background&width=400&height=500&seq=gallery-dw-3&orientation=portrait"
                  alt="Restaurant Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20interior%20painting%20urban%20apartment%20De%20Waterkant%20soft%20colors%20professional%20finish%20comfortable%20atmosphere%20simple%20background&width=400&height=500&seq=gallery-dw-4&orientation=portrait"
                  alt="Bedroom Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=heritage%20Victorian%20building%20restored%20painted%20De%20Waterkant%20historic%20property%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-dw-5&orientation=portrait"
                  alt="Heritage Building Restoration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20office%20space%20painted%20De%20Waterkant%20professional%20finish%20clean%20design%20business%20environment%20simple%20background&width=400&height=500&seq=gallery-dw-6&orientation=portrait"
                  alt="Office Space Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Client Reviews</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters De Waterkant did an exceptional job on our apartment. They were professional, efficient, and the quality of work is outstanding. They worked around our schedule and kept everything clean. Highly recommend their services!"</p>
                <p className="font-semibold">- Sophie M., De Waterkant</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We hired Painters De Waterkant for our restaurant in Green Point. They worked after hours to avoid disrupting our business and delivered beautiful results. Their attention to detail is impressive. Excellent service!"</p>
                <p className="font-semibold">- Marco R., Green Point</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Excellent work on our heritage townhouse! Painters De Waterkant understood the requirements for working with a heritage property and delivered beautiful results. Very professional and knowledgeable team!"</p>
                <p className="font-semibold">- James T., Tamboerskloof</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters De Waterkant painted our boutique hotel and the transformation is incredible. They worked efficiently, maintained high standards, and the quality is exceptional. Our guests love the new look!"</p>
                <p className="font-semibold">- Linda K., Sea Point</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We're thrilled with the interior painting work done by Painters De Waterkant. Their color consultation service was invaluable, and the painters were meticulous. Our apartment looks stunning! Best painting company in the area!"</p>
                <p className="font-semibold">- Alexandra P., De Waterkant</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Property?</h2>
          <p className="text-xl mb-8">Get a free quote from Painters De Waterkant today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
            <a href="http://paintersdewaterkant.co.za" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Visit Website</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#e9f638] text-black py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cape Town Painters</h3>
              <p className="text-sm">Your trusted directory for professional painting services across South Africa.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/get-a-quote" className="hover:underline">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Residential Painting</li>
                <li>Commercial Painting</li>
                <li>Heritage Buildings</li>
                <li>Interior & Exterior</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Cape Town, South Africa
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-black/20 pt-8 text-center text-sm">
            <p>&copy; 2024 Cape Town Painters Directory. All rights reserved.</p>
            <div className="mt-2 space-x-4">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Powered by leaderr</a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}